// src/pages/portal/reporting/eod/EodExportMenu.js
// The EOD page's Export control: one report payload, three outputs.
//
// Print paths use the portal's own print-window idiom (window.open + an inline
// window.print(), as in PortalPayRunDetailPage and the payslip flow) rather
// than PrintingUtils.printWithIframe. PrintingUtils is POS machinery — it
// resolves per-device station config and can route to the Electron main
// process, none of which exists or applies in a browser portal session.
import React, { useEffect, useRef, useState } from 'react';
import { eodReportService, printingService } from '../../../../services/api';
// `sendPayload`, not `sendToPrinter`: it branches on payload.protocol and
// sends `data_base64` over the raw socket for escpos, or `xml` over HTTP for
// ePOS. Calling the ePOS transport directly is how this file came to send an
// empty document — `_encode_for` sets `xml` to "" for an escpos printer and
// puts the bytes in `data_base64`.
import { sendPayload } from '../../../../utils/printTransport';
import { isRawPrintAvailable } from '../../../../utils/escposClient';
import { downloadCsvContent } from '../../../../components/bulkImport/csvUtils';
import { C } from '../../../../styles/theme';
import { FONT } from './EodCashSections';
import {
    buildEodCsv, buildEodA4Html, buildEodReceiptHtml, eodFilename,
} from './eodFormats';

// How long to wait on the printer lookup before deciding this session has no
// LAN Epson to talk to. The backend cold-starts, and a manager who is off-site
// will never reach the printer anyway — falling back to a local print window is
// the useful answer, not a spinner.
const LIST_PRINTERS_TIMEOUT_MS = 2000;

const openPrintWindow = (html) => {
    const win = window.open('', '_blank');
    if (!win) return false;   // pop-up blocked
    win.document.write(html);
    win.document.close();
    return true;
};

// The print window triggers itself on load, matching the payslip/payrun flow.
const withAutoPrint = (html) =>
    html.replace('</body>', '<script>window.onload=function(){window.print()}</script></body>');

const resolveEpsonReceiptPrinter = async () => {
    try {
        const printers = await printingService.listPrinters({ timeout: LIST_PRINTERS_TIMEOUT_MS });
        // Protocols THIS SURFACE can dial — deliberately narrower than the
        // app-wide DIRECT_PRINT_PROTOCOLS list, which is broader than a
        // browser.
        //
        //   ePOS    = HTTP POST to the printer's web service -> browser can
        //   ESC/POS = raw bytes on a TCP socket, port 9100   -> native only
        //
        // escposClient.isRawPrintAvailable() is literally
        // Capacitor.isNativePlatform(). This menu is mounted only by
        // PortalEodPage — the web portal — so an escpos printer is genuinely
        // undialable here and falling through to the print window below is
        // correct, not a defect.
        //
        // 10 Sep: this was briefly widened to the shared list on the theory
        // that the narrowness was drift. It was not; it is load-bearing.
        // Widening it made the menu resolve a printer it cannot reach,
        // enqueue a PrintJob, fail at the socket and fall back anyway.
        // Gated on the capability rather than a literal so it stays correct
        // if the native manager app ever mounts this menu.
        return (printers || []).find(
            p => p.role === 'receipt'
                && p.is_active
                && (p.protocol === 'epson'
                    || (p.protocol === 'escpos' && isRawPrintAvailable()))
        ) || null;
    } catch {
        // No printer list means no direct path — the caller falls back to a
        // local print window rather than dead-ending.
        return null;
    }
};

const EodExportMenu = ({ report, t, isMobile }) => {
    const [open, setOpen] = useState(false);
    const [busy, setBusy] = useState(false);
    const [message, setMessage] = useState(null);
    const wrapRef = useRef(null);

    const disabled = !report || busy;

    useEffect(() => {
        if (!open) return undefined;
        const onKey = (e) => { if (e.key === 'Escape') setOpen(false); };
        const onClick = (e) => {
            if (wrapRef.current && !wrapRef.current.contains(e.target)) setOpen(false);
        };
        window.addEventListener('keydown', onKey);
        document.addEventListener('mousedown', onClick);
        return () => {
            window.removeEventListener('keydown', onKey);
            document.removeEventListener('mousedown', onClick);
        };
    }, [open]);

    // A print that silently does nothing is the worst outcome here, so every
    // path either confirms or says why not.
    const notify = (tone, text) => {
        setMessage({ tone, text });
        window.setTimeout(() => setMessage(null), 6000);
    };

    const printLocalReceipt = () => {
        const ok = openPrintWindow(withAutoPrint(buildEodReceiptHtml(report, t)));
        if (!ok) notify('bad', t('popup_blocked') || 'Allow pop-ups to print this report.');
    };

    const handlePrintReceipt = async () => {
        setOpen(false);
        setBusy(true);
        try {
            const printer = await resolveEpsonReceiptPrinter();
            if (!printer) {
                printLocalReceipt();
                return;
            }
            const payload = await eodReportService.getEodPrintPayload(report.sales_date, printer.id);
            const result = await sendPayload(payload);
            if (result && result.success) {
                notify('good', t('sent_to_printer') || 'Sent to printer.');
            } else {
                // The printer is configured but unreachable — most likely the
                // manager is not on the store network. Give them paper anyway.
                notify('warn', t('printer_unreachable_using_window')
                    || 'Printer unreachable — opening a print window instead.');
                printLocalReceipt();
            }
        } catch (err) {
            console.error('EOD receipt print failed:', err);
            printLocalReceipt();
        } finally {
            setBusy(false);
        }
    };

    const handlePrintPdf = () => {
        setOpen(false);
        const ok = openPrintWindow(withAutoPrint(buildEodA4Html(report, t)));
        if (!ok) notify('bad', t('popup_blocked') || 'Allow pop-ups to print this report.');
    };

    const handleDownloadCsv = () => {
        setOpen(false);
        try {
            downloadCsvContent(buildEodCsv(report), eodFilename(report, 'csv'));
        } catch (err) {
            console.error('EOD CSV export failed:', err);
            notify('bad', t('export_failed') || 'Export failed.');
        }
    };

    const items = [
        { key: 'receipt', label: t('print_receipt') || 'Print receipt', onClick: handlePrintReceipt },
        { key: 'pdf', label: t('print_save_pdf') || 'Print / Save PDF', onClick: handlePrintPdf },
        { key: 'csv', label: t('download_csv') || 'Download CSV', onClick: handleDownloadCsv },
    ];

    const toneColors = {
        good: { background: C.greenLight, color: C.greenDark, border: C.greenBorder },
        warn: { background: C.faint, color: C.text, border: C.border },
        bad: { background: C.redLight, color: C.red, border: C.redBorder },
    };

    return (
        <div ref={wrapRef} style={{ position: 'relative', width: isMobile ? '100%' : 'auto' }}>
            <button
                onClick={() => setOpen(!open)}
                disabled={disabled}
                title={!report ? (t('eod_nothing_to_export') || 'No report for this business day yet') : undefined}
                style={{
                    display: 'inline-flex', alignItems: 'center', justifyContent: 'center', gap: 8,
                    width: isMobile ? '100%' : 'auto',
                    padding: isMobile ? '12px 16px' : '8px 16px',
                    borderRadius: 8, fontSize: 12, fontWeight: 700, fontFamily: FONT,
                    cursor: disabled ? 'not-allowed' : 'pointer',
                    background: disabled ? C.faint : C.green,
                    color: disabled ? C.mutedLight : C.white,
                    border: `1px solid ${disabled ? C.border : C.green}`,
                    opacity: busy ? 0.7 : 1, transition: 'all 0.15s',
                }}
            >
                {busy ? (t('working') || 'Working…') : (t('export') || 'Export')}
                <span style={{ fontSize: 9 }}>&#9660;</span>
            </button>

            {open && !disabled && (
                <div style={{
                    position: 'absolute', top: 'calc(100% + 6px)', right: 0, zIndex: 50,
                    minWidth: 220, background: C.white, borderRadius: 10,
                    border: `1px solid ${C.border}`, boxShadow: '0 12px 32px rgba(0,0,0,0.12)',
                    overflow: 'hidden', fontFamily: FONT,
                }}>
                    {items.map((item) => (
                        <button
                            key={item.key}
                            onClick={item.onClick}
                            onMouseEnter={(e) => { e.currentTarget.style.background = C.faint; }}
                            onMouseLeave={(e) => { e.currentTarget.style.background = C.white; }}
                            style={{
                                display: 'block', width: '100%', textAlign: 'left',
                                padding: '11px 16px', fontSize: 13, fontWeight: 600,
                                color: C.text, background: C.white, border: 'none',
                                cursor: 'pointer', fontFamily: FONT,
                            }}
                        >{item.label}</button>
                    ))}
                </div>
            )}

            {message && (
                <div style={{
                    position: 'absolute', top: 'calc(100% + 6px)', right: 0, zIndex: 60,
                    minWidth: 220, maxWidth: 320, padding: '10px 14px', borderRadius: 8,
                    fontSize: 12, fontWeight: 600, fontFamily: FONT,
                    ...toneColors[message.tone],
                    border: `1px solid ${toneColors[message.tone].border}`,
                }}>
                    {message.text}
                </div>
            )}
        </div>
    );
};

export default EodExportMenu;
